import { useState, useEffect, useCallback } from "react";
import { MdModeEdit } from "react-icons/md";
import {
  useAddVendorTagMutation,
  useUpdateVendorTagMutation,
  useGetVendorTagByIdQuery,
  useCheckTagUniqueNameQuery,
  useGetAllServiceTypesMutation,
} from "../../api/api";
import { useToast } from "../../@/components/ui/use-toast";
import { Plus, X } from "lucide-react";
import SkeletonAddEditCategoryDialog from "../../skeletonLoading/SkeletonAddEditCategoryDialog";
import { MAX_VENDOR_TAGNAME_LENGTH_MAX, TAGS } from "../../lib/constants";
import useDebounce from "../../lib/utils";
import GenericDropdown from "../../components/GenericDropDown";

import SortupDialog from "../../components/common/SortupDialog";
type AddEditVendorTagDialogProps = {
  vendorTagId?: number;
  isEditOnly: boolean;
  fetchData: () => void;
  setPageNumber: (pageNumber: number) => void;
  setSearchTerm: (searchTerm: string) => void;
  selectedServiceType: string;
};

interface VendorTag {
  name: string;
  serviceTypeId: number;
}
interface Service {
  name: string;
  serviceTypeId: number;
}

export function AddEditVendorTagDialog({
  vendorTagId,
  isEditOnly,
  fetchData,
  setSearchTerm,
  setPageNumber,
  selectedServiceType,
}: AddEditVendorTagDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [vendorTagValues, setVendorTagValues] = useState<VendorTag>({
    name: "",
    serviceTypeId: selectedServiceType ? parseInt(selectedServiceType, 10) : 0,
  });
  const [serviceValue, setServiceValue] = useState<Service>({
    name: "",
    serviceTypeId: selectedServiceType ? parseInt(selectedServiceType, 10) : 0,
  });
  const debouncedServiceSearchQuery = useDebounce(serviceValue.name, 200);
  const [serviceTypesOptions, setServiceTypesOptions] = useState<any[]>([]);
  const [isServiceTypesLoading, setIsServiceTypesLoading] = useState(false);
  const debouncedVendorTagValues = useDebounce(vendorTagValues.name, 200);
  const [isTagUniqueError, setIsTagUniqueError] = useState(false);
  const [originalTagName, setOriginalTagName] = useState<string>("");

  const toast = useToast();

  const shouldFetchVendorTag = isDialogOpen && isEditOnly;

  const { data, isLoading, isSuccess } = useGetVendorTagByIdQuery(
    { vendorTagId: vendorTagId || 0 },
    { skip: !shouldFetchVendorTag }
  );

  const shouldCheckTagUniqueName =
    debouncedVendorTagValues &&
    vendorTagValues.name.trim() !== "" &&
    (vendorTagValues.name !== originalTagName ||
      vendorTagValues.serviceTypeId !== data?.data?.serviceTypeId);

  const { data: isVendorTagUniqueData, isLoading: isVendorTagUniqueLoading } =
    useCheckTagUniqueNameQuery(
      {
        tagName: debouncedVendorTagValues,
        tagType: "vendor",
        serviceTypeId: vendorTagValues.serviceTypeId,
        isUpdateCall: vendorTagId ? true : false,
        tagId: vendorTagId,
      },
      { skip: !debouncedVendorTagValues || !shouldCheckTagUniqueName }
    );

  const [AddVendorTag, { isLoading: isAddVendorTagLoading }] =
    useAddVendorTagMutation();

  const [UpdateVendorTag, { isLoading: isUpdateVendorTagLoading }] =
    useUpdateVendorTagMutation();

  useEffect(() => {
    if (isSuccess && data) {
      setVendorTagValues({
        name: data.data.name,
        serviceTypeId:
          data.data.serviceTypeId || parseInt(selectedServiceType, 10),
      });
      setOriginalTagName(data.data.name);
      setServiceValue((prev) => ({
        ...prev,
        serviceTypeId:
          data.data.serviceTypeId || parseInt(selectedServiceType, 10),
      }));
    }
  }, [isSuccess, data, selectedServiceType]);

  useEffect(() => {
    if (isVendorTagUniqueData && !isVendorTagUniqueData?.data.isUnique) {
      setIsTagUniqueError(true);
    } else {
      setIsTagUniqueError(false);
    }
  }, [isVendorTagUniqueData]);

  const [getServiceTypes, { data: serviceTypes }] =
    useGetAllServiceTypesMutation();
  const fetchAllServices = useCallback(async () => {
    setIsServiceTypesLoading(true);
    try {
      const response: any = await getServiceTypes({
        getAllServiceTypesRequestDto: {
          searchQuery:
            debouncedServiceSearchQuery !== ""
              ? debouncedServiceSearchQuery
              : undefined,
          pageNumber: 1,
          pageSize: 100,
          getTotalCount: true,
          sortingColumn: "name",
          sortingOrder: "asc",
          //  isDistanceRangApplicable: true,
        },
      }).unwrap();
      const serviceTypeOptions = response.data?.serviceTypes?.map(
        (type: any) => ({
          value: type.id.toString(),
          label: type.name,
        })
      );
      setServiceTypesOptions(serviceTypeOptions || []);
    } catch (err) {
      console.error("Failed to fetch service types:", err);
    } finally {
      setIsServiceTypesLoading(false);
    }
  }, [debouncedServiceSearchQuery, getServiceTypes]);
  useEffect(() => {
    if (isDialogOpen) {
      fetchAllServices();
    }
  }, [isDialogOpen, fetchAllServices]);
  const handleServiceTypeChange = (value: string) => {
    const serviceTypeId = parseInt(value, 10);
    setServiceValue((prev) => ({
      ...prev,
      serviceTypeId: serviceTypeId,
    }));
    setVendorTagValues((prev) => ({
      ...prev,
      serviceTypeId: serviceTypeId,
    }));
  };
  const handleVendorTagNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value.length <= MAX_VENDOR_TAGNAME_LENGTH_MAX) {
      setVendorTagValues({ ...vendorTagValues, name: value });
    } else {
      setVendorTagValues({
        ...vendorTagValues,
        name: value.slice(0, MAX_VENDOR_TAGNAME_LENGTH_MAX),
      });
    }
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
    if (!isEditOnly) {
      setVendorTagValues({
        name: "",
        serviceTypeId: selectedServiceType
          ? parseInt(selectedServiceType, 10)
          : 0,
      });
      setServiceValue({
        name: "",
        serviceTypeId: selectedServiceType
          ? parseInt(selectedServiceType, 10)
          : 0,
      });
      setIsTagUniqueError(false);
    }
  };

  const handleAddVendorTag = async () => {
    if (isFormDisabled()) return;

    try {
      const response: any = await AddVendorTag({
        body: {
          name: vendorTagValues.name.trim(),
          serviceTypeId: serviceValue.serviceTypeId,
          parentId: TAGS.VendorTags,
        },
      });

      if ("data" in response && response?.data?.message.includes("success")) {
        setIsDialogOpen(false);
        setIsTagUniqueError(false);
        toast.toast({
          title: "Success!",
          description: "Vendor tag added successfully.",
        });
        setPageNumber(1);
        setSearchTerm("");
        fetchData();
      } else {
        toast.toast({
          title: "Error!",
          description: `${
            response?.error?.data?.message || "Failed to add vendor tag."
          }`,
        });
      }
    } catch (error: any) {
      toast.toast({
        title: "Error!",
        description: error.message || "Failed to add vendor tag.",
      });
    }
  };

  const handleEditVendorTag = async () => {
    if (!isVendorTagUniqueData || isVendorTagUniqueLoading) return;

    if (vendorTagValues.name !== originalTagName) {
      if (isVendorTagUniqueData && !isVendorTagUniqueData.data.isUnique) {
        toast.toast({
          title: "Error!",
          description: "Vendor tag name already exists.",
        });
        return;
      }
    }

    if (serviceValue.serviceTypeId <= 0) {
      toast.toast({
        title: "Error!",
        description: "Service type is invalid.",
      });
      return;
    }

    try {
      const response: any = await UpdateVendorTag({
        body: {
          name: vendorTagValues.name,
          serviceTypeId: serviceValue.serviceTypeId,
          id: vendorTagId || 0,
        },
      });

      if ("data" in response && response?.data?.message.includes("success")) {
        setIsDialogOpen(false);
        setIsTagUniqueError(false);
        toast.toast({
          title: "Success!",
          description: "Vendor tag updated successfully.",
        });
        setPageNumber(1);
        setSearchTerm("");
        fetchData();
      } else {
        throw new Error(
          response?.error?.data?.message || "Failed to update vendor tag."
        );
      }
    } catch (error: any) {
      toast.toast({
        title: "Error!",
        description: error.message || "Failed to update vendor tag.",
      });
    }
  };

  const isFormDisabled = () => {
    if (
      isAddVendorTagLoading ||
      isUpdateVendorTagLoading ||
      isVendorTagUniqueLoading
    ) {
      return true;
    }

    if (vendorTagValues.name.trim() === "" || serviceValue.serviceTypeId <= 0) {
      return true;
    }

    if (isTagUniqueError) {
      return true;
    }

    if (isEditOnly) {
      return (
        vendorTagValues.name === originalTagName &&
        serviceValue.serviceTypeId === data?.data?.serviceTypeId
      );
    }

    return false;
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        {isEditOnly ? (
          <div onClick={handleDialogOpen}>
            <MdModeEdit
              size={16}
              className="cursor-pointer text-[#72767E] hover:text-black"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDialogOpen}
            className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 gap-1 rounded-lg w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Add Vendor Tag
          </button>
        )}
      </div>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header={isEditOnly ? "Edit Vendor Tag" : "Add Vendor Tag"}
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: isEditOnly ? "Update" : "Add",
            onClick: isEditOnly ? handleEditVendorTag : handleAddVendorTag,
            className: `${
              isFormDisabled()
                ? "opacity-50 cursor-not-allowed bg-customGreenColor"
                : "bg-customGreenColor hover:bg-customGreenColor/90"
            } text-white`,
            disabled: isFormDisabled(),
          },
          {
            name: "Cancel",
            className: "bg-white text-gray-900 border hover:bg-gray-100",
            disabled: isAddVendorTagLoading || isUpdateVendorTagLoading,
            onClick: () => setIsDialogOpen(false),
          },
        ]}
      >
        {isLoading || isServiceTypesLoading ? (
          <SkeletonAddEditCategoryDialog rows={2} />
        ) : (
          <>
            <div className="mt-4 px-2">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="vendorTagName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Vendor Tag Name
                </label>
                <input
                  type="text"
                  id="vendorTagName"
                  value={vendorTagValues.name}
                  onChange={handleVendorTagNameChange}
                  className={`focus:outline-customGreenColor mt-1 p-2 border ${
                    isTagUniqueError ? "border-red-500" : "border-gray-300"
                  } rounded-md w-full`}
                />
                {isTagUniqueError && (
                  <p className="text-red-500 text-sm mt-1">
                    Vendor tag name already exists.
                  </p>
                )}
                <span className="text-xs text-gray-500">
                  {vendorTagValues.name.length}/{MAX_VENDOR_TAGNAME_LENGTH_MAX}
                </span>
              </div>
              <label
                htmlFor="serviceTypeId"
                className="block mt-4 text-sm font-medium text-gray-700 mb-1"
              >
                Service Type
              </label>
              <GenericDropdown
                labelValue={
                  serviceTypesOptions.find(
                    (option) =>
                      option.value === serviceValue.serviceTypeId.toString()
                  )?.label || ""
                }
                options={serviceTypesOptions}
                onChange={handleServiceTypeChange}
                placeholder="Select Service Type"
                error={false}
                className="w-full justify-between"
                dropdownMenuContentWidth="w-[34vw]"
              />
            </div>
          </>
        )}
      </SortupDialog>
    </>
  );
}
